import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../store";
import { getShoppingVouchers } from "../../../../Redux/referral/slice";
import {
  CalendarDays,
  ChevronRight,
  Copy,
  Gift,
  Search,
  TicketPercent,
  X,
} from "lucide-react";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import {
  formatVoucherCurrency,
  formatVoucherDate,
  formatVoucherLongDate,
  mapVoucherItemToRecord,
  toClassSlug,
  VoucherRecord,
} from "../stuffAdapters";
import "./ShoppingVoucherPage.css";

const SalarShopVoucher = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { shoppingVouchers } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(
      getShoppingVouchers({
        page,
        limit,
        status: "ALL",
        source: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange]);

  const { data: vouchers, pagination, wallet, loading } = shoppingVouchers;

  const copyVoucherCode = async (voucher: VoucherRecord) => {
    try {
      await navigator.clipboard.writeText(voucher.code);
      setCopiedId(voucher.id);
      window.setTimeout(() => setCopiedId(null), 1300);
    } catch {
      setCopiedId(null);
    }
  };

  const totalPages = pagination?.totalPages || 0;

  const renderVoucherDetails = (voucher: VoucherRecord) => (
    <div className="shopping-voucher-details shopping-voucher-details-desktop">
      <div className="shopping-voucher-detail-main">
        <div className="shopping-voucher-detail-image">
          <img src={voucher.image} alt={voucher.title} />
        </div>
        <div className="shopping-voucher-detail-copy">
          <span className="shopping-voucher-detail-kicker">
            PURCHASED ON {formatVoucherLongDate(voucher.purchasedOn)}
          </span>
          <h2>{voucher.title}</h2>
          <p>{voucher.description}</p>
          <div className="shopping-voucher-detail-actions">
            <button
              type="button"
              className="primary"
              onClick={() => navigate(`/ShoppingVoucherDetails/${voucher.id}`)}
            >
              View voucher details
            </button>
          </div>
        </div>
      </div>

      <div className="shopping-voucher-facts">
        <div>
          <span>Grand Total</span>
          <strong>{formatVoucherCurrency(voucher.grandTotal)}</strong>
        </div>
        <div>
          <span>Salar Shopping Voucher</span>
          <strong>{formatVoucherCurrency(voucher.voucherValue)}</strong>
        </div>
        <div>
          <span>Valid From</span>
          <strong>{formatVoucherDate(voucher.validFrom)}</strong>
        </div>
        <div>
          <span>Valid To</span>
          <strong>{formatVoucherDate(voucher.validTo)}</strong>
        </div>
      </div>

      <div className="shopping-voucher-code-card">
        <div>
          <span>VOUCHER CODE</span>
          <strong>{voucher.code}</strong>
        </div>
        <button type="button" onClick={() => copyVoucherCode(voucher)}>
          <Copy size={16} strokeWidth={2} />
          {copiedId === voucher.id ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );

  return (
    <section className="shopping-voucher-page">
      <div className="shopping-voucher-shell">
        <header className="shopping-voucher-header">
          <div className="shopping-voucher-heading">
            <span className="shopping-voucher-heading-icon">
              <TicketPercent size={23} strokeWidth={2} />
            </span>
            <div>
              <span className="shopping-voucher-kicker">MY ACCOUNT</span>
              <h1>Salar Shopping Voucher</h1>
              <p>
                View purchased vouchers, validity and redemption details
                in one place.
              </p>
            </div>
          </div>
          {wallet && (
            <span className="shopping-voucher-result-pill">
              Balance:{" "}
              {formatVoucherCurrency(wallet.availableBalance || 0)}
            </span>
          )}
        </header>

        <div className="shopping-voucher-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Vouchers per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option key={val} value={val}>
                Last {val} Entries
              </option>
            ))}
          </select>

          <div className="shopping-voucher-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <span className="shopping-voucher-result-pill">
            {pagination?.total ?? vouchers?.length ?? 0} vouchers
          </span>

          <div className="shopping-voucher-search">
            <Search size={17} strokeWidth={2} />
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search vouchers..."
              aria-label="Search shopping vouchers"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <div className="shopping-voucher-list">
          {loading ? (
            <div className="shopping-voucher-empty">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : vouchers && vouchers.length > 0 ? (
            vouchers.map((item) => {
              const voucher = mapVoucherItemToRecord(item);
              return (
                <article
                  className="shopping-voucher-card"
                  key={voucher.id}
                >
                  <div className="shopping-voucher-card-meta">
                    <div>
                      <span>Date</span>
                      <strong>
                        {formatVoucherDate(voucher.purchasedOn)}
                      </strong>
                    </div>
                    <div>
                      <span>Order No</span>
                      <strong>{voucher.orderNo}</strong>
                    </div>
                    <div>
                      <span>Payment Type</span>
                      <strong className="shopping-voucher-online">
                        {voucher.paymentType}
                      </strong>
                    </div>
                    <div className="shopping-voucher-status-meta">
                      <span>Voucher Status</span>
                      <strong
                        className={`shopping-voucher-status shopping-voucher-status-${toClassSlug(
                          voucher.status,
                        )}`}
                      >
                        {voucher.status}
                      </strong>
                    </div>
                  </div>

                  <div className="shopping-voucher-desktop-content">
                    {renderVoucherDetails(voucher)}
                  </div>

                  <button
                    type="button"
                    className="shopping-voucher-mobile-summary"
                    onClick={() => navigate(`/ShoppingVoucherDetails/${voucher.id}`)}
                  >
                    <div className="shopping-voucher-mobile-image">
                      <img src={voucher.image} alt="" />
                    </div>
                    <div className="shopping-voucher-mobile-copy">
                      <div className="shopping-voucher-mobile-topline">
                        <span
                          className={`shopping-voucher-status shopping-voucher-status-${toClassSlug(
                            voucher.status,
                          )}`}
                        >
                          {voucher.status}
                        </span>
                        <span className="shopping-voucher-mobile-date">
                          <CalendarDays size={13} strokeWidth={2} />
                          {formatVoucherDate(voucher.purchasedOn)}
                        </span>
                      </div>
                      <h2>{voucher.title}</h2>
                      <p>{voucher.description}</p>
                      <strong>
                        Purchased on{" "}
                        {formatVoucherLongDate(voucher.purchasedOn)}
                      </strong>
                    </div>
                    <ChevronRight
                      className="shopping-voucher-mobile-chevron"
                      size={20}
                      strokeWidth={2}
                    />
                  </button>
                </article>
              );
            })
          ) : (
            <div className="shopping-voucher-empty">
              <Gift size={34} strokeWidth={1.8} />
              <strong>No shopping vouchers found</strong>
              <span>Try another search or date range.</span>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <footer className="shopping-voucher-pagination">
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <div>
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage(Math.max(1, page - 1))}
                aria-label="Previous page"
              >
                <ChevronRight
                  size={18}
                  style={{ transform: "rotate(180deg)" }}
                />
              </button>
              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                aria-label="Next page"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </footer>
        )}
      </div>
    </section>
  );
};

export default SalarShopVoucher;
